import { useEffect, useState } from 'react';
import { IdentifiableObject } from '../../../stores/models/OptionCollection';
import { TableController } from '../../../stores/models/TableController';
import useDebouncedEffect from './DebouncedEffect';

export default function useLoadTableData<
    IdentityKey extends string,
    Record extends IdentifiableObject<IdentityKey>,
    StubRecord extends IdentifiableObject<IdentityKey> = Record,
    MergedRecord extends object = Record
>(
    tableController: TableController<IdentityKey, Record, StubRecord, MergedRecord>
) {

    const offset = tableController.tableState.paginationState.offset;
    const limit = tableController.tableState.paginationState.rowsPerPage;

    const [initialLoad, setInitialLoad] = useState(true);
    const [latestLoadRequest, setLatestLoadRequest] = useState(JSON.stringify(tableController.tableState.loadRequest));

    const loadData = async (resetRecords?: boolean) => {
        try {
            await tableController.load(resetRecords);
        } catch (error) {
            if (error && typeof error === 'object' && 'code' in error && (error as { code: any }).code === 'ERR_CANCELED') {
                console.log(error)
            } else {
                throw error;
            }
        }
    }

    const reload = () => {
        loadData(true);
    }

    useEffect(() => {
        if (tableController.records.options.length === 0) {
            loadData();
            setLatestLoadRequest(JSON.stringify(tableController.tableState.loadRequest));
        }
    }, []);

    useEffect(() => {
        const currentLoadRequest = tableController.tableState.loadRequest;
        const parsedLatestLoadRequest = JSON.parse(latestLoadRequest);
        const limitChanged = parsedLatestLoadRequest.limit !== currentLoadRequest.limit;
        const offsetChanged = parsedLatestLoadRequest.offset !== currentLoadRequest.offset;
        const sortingChanged = JSON.stringify(parsedLatestLoadRequest.sort) !== JSON.stringify(currentLoadRequest.sort);
        const filtersChanged = JSON.stringify(parsedLatestLoadRequest.filterState?.filters) !== JSON.stringify(currentLoadRequest.filterState?.filters);

        // Reload triggers:
        // 1. The limit changed
        // 2. The sorting changed
        // 3. The filters changed
        // 4. The search query changed

        // Load next window:
        // 1. The offset changed and nothing else changed and there are records left to load

        const shouldReload = limitChanged || sortingChanged || filtersChanged;
        if (shouldReload) {
            reload();
            setLatestLoadRequest(JSON.stringify(currentLoadRequest));
        } else if (offsetChanged && offset + limit > tableController.records.options.length && tableController.hasRecordsToLoad) {
            loadData();
            setLatestLoadRequest(JSON.stringify(currentLoadRequest));
        }
    }, [JSON.stringify(tableController.tableState.loadRequest)]);


    useDebouncedEffect({
        trigger: tableController.tableState.loadRequest.filterState?.searchQuery,
        effect: () => {
            if (initialLoad) {
                setInitialLoad(false);
            } else {
                reload();
                setLatestLoadRequest(JSON.stringify(tableController.tableState.loadRequest));
            }
        }
    });
}